// Tracks which kind of device the user is interacting with right now:
// mouse, keyboard, or gamepad. Components subscribe to render matching
// button-prompt glyphs and to decide whether to show the virtual aim
// reticle.
//
// A 250 ms debounce avoids glyph flicker when a stray mouse jiggle
// happens mid-match on a controller. We only update on sustained
// input from the new source.

const SOURCES = new Set(['mouse', 'keyboard', 'gamepad']);
const DEBOUNCE_MS = 250;

export function createInputSource({
  initial = 'mouse',
  now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now()),
} = {}) {
  let current = initial;
  let pendingSource = null;
  let pendingSince = 0;
  const listeners = new Set();

  function notify() {
    for (const fn of listeners) fn(current);
  }

  function report(source) {
    if (!SOURCES.has(source)) return;
    const t = now();
    if (source === current) {
      pendingSource = null;
      return;
    }
    // Mouse and keyboard never debounce against each other. That's a
    // deliberate multi-input switch (alt-tabbing, etc.), not an
    // accident. Only the bounce away from gamepad needs the delay.
    const wasGamepad = current === 'gamepad';
    const becomingGamepad = source === 'gamepad';
    const needsDebounce = wasGamepad && !becomingGamepad;
    if (!needsDebounce) {
      current = source;
      pendingSource = null;
      notify();
      return;
    }
    if (pendingSource !== source) {
      pendingSource = source;
      pendingSince = t;
      return;
    }
    if (t - pendingSince >= DEBOUNCE_MS) {
      current = source;
      pendingSource = null;
      notify();
    }
  }

  return {
    get() { return current; },
    report,
    subscribe(fn) {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    // Test helper; not part of the runtime API but also harmless to keep.
    _setForTest(source) {
      if (!SOURCES.has(source)) return;
      current = source;
      pendingSource = null;
      notify();
    },
  };
}

// Module-level singleton. Tests use createInputSource() directly for
// isolation; runtime code imports `inputSource`.
export const inputSource = createInputSource();
